import React, { Suspense, lazy, useState, useMemo } from 'react';
import Button from '../../../ui-kit/Button';
import Sidebar from './Sidebar';
import Header from './Header';
import { ChatSystem } from '../../chat';
import { ChatList, ChatWindow } from '../../chat'; // For full page chat
import { useChat } from '../../chat/hooks/useChat';
import { CustomerList, UploadCustomers, CustomerProfile, CustomerAnalytics, CustomersProvider } from '../../customers';
import LiveCallDock from '../../customers/ui/LiveCallDock';
import LiveCallLayer from '../../customers/ui/LiveCallLayer';
import PhoneStatusPill from '../../customers/ui/PhoneStatusPill';
import { getViewMode, setViewMode, isNarrowScreen } from '../../../shared/utils/desktopViewport';
import DialTracker from '../../customers/ui/DialTracker';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import MobileNavbar from '../../../mobile/components/MobileNavbar';
import useReminderSystem from '../../todos/hooks/useReminderSystem';
import AlarmOverlay from '../../todos/ui/AlarmOverlay';
import NotificationManager from '../../notifications/ui/NotificationManager';
import NotificationBell from '../../notifications/ui/NotificationBell';
import { ChatNotificationProvider } from '../../chat/ui/ChatNotificationToast';
import { can, canUser } from '../../../core/permissions';
import { lazyWithReload } from '../../../shared/utils/lazyWithReload';
import useOrderStore from '../../orders/store/orderStore';
import useTeamAppManifest from '../../../mobile/hooks/useTeamAppManifest';

// --- Lazy-loaded tab components (code-split per route/tab) ---
const DashboardOverview = lazyWithReload(() => import('./DashboardOverview'));
const OrderList = lazyWithReload(() => import('../../orders').then(m => ({ default: m.OrderList })));
const ParserInterface = lazyWithReload(() => import('../../parser').then(m => ({ default: m.ParserInterface })));
const ProductsInterface = lazyWithReload(() => import('../../products').then(m => ({ default: m.ProductsInterface })));
const SettingsInterface = lazyWithReload(() => import('../../userSettings').then(m => ({ default: m.SettingsInterface })));
const AnalyticsDashboard = lazyWithReload(() => import('../../chat/ui/Analytics/AnalyticsDashboard'));
const InteraktAnalytics = lazyWithReload(() => import('../../analytics/ui/InteraktAnalytics'));
const IndiaPostManager = lazyWithReload(() => import('../../orders/ui/IndiaPostManager'));
const DTDCManager = lazyWithReload(() => import('../../orders/ui/DTDCManager'));
const EkartManager = lazyWithReload(() => import('../../orders/ui/EkartManager'));
const ShiprocketManager = lazyWithReload(() => import('../../orders/ui/ShiprocketManager'));
const ArchivedOrders = lazyWithReload(() => import('../../orders/ui/ArchivedOrders'));
const CancelledOrders = lazyWithReload(() => import('../../orders/ui/CancelledOrders'));
const AbandonedOrders = lazyWithReload(() => import('../../orders/ui/AbandonedOrders'));
const RewardsModule = lazyWithReload(() => import('../../rewards/ui/RewardsModule'));
const FinanceLayout = lazyWithReload(() => import('../../finance/FinanceLayout'));
const ReferralDashboard = lazyWithReload(() => import('../../referrals').then(m => ({ default: m.ReferralDashboard })));
const TaskBoard = lazyWithReload(() => import('../../todos/ui/TaskBoard'));
const OrganizationCalendar = lazyWithReload(() => import('../../calendar/ui/OrganizationCalendar'));
const StaffContainer = lazyWithReload(() => import('../../staff/ui/StaffContainer').then(m => ({ default: m.StaffContainer })));
const ComplaintsLayout = lazyWithReload(() => import('../../complaints').then(m => ({ default: m.ComplaintsLayout })));
const QuickOrderAdmin = lazyWithReload(() => import('../../storefront/ui/QuickOrderAdmin'));
const CourierEscalationDashboard = lazyWithReload(() => import('../../escalations/ui/CourierEscalationDashboard'));
const RtdbUsagePanel = lazyWithReload(() => import('../../diagnostics/ui/RtdbUsagePanel'));
const BackupRestorePanel = lazyWithReload(() => import('../../backup/ui/BackupRestorePanel'));
const TrackingDashboard = lazyWithReload(() => import('../../tracking/ui/TrackingDashboard'));
const CustomerInsights = lazyWithReload(() => import('../../insights/ui/CustomerInsights'));
const TelecallerWorkspace = lazyWithReload(() => import('../../customers/ui/TelecallerWorkspace'));
const ApprovalDesk = lazyWithReload(() => import('../../approvals/ui/ApprovalDesk'));

// Screens that never read the shared orders tree: opening only these never starts the
// ~9.5 MB orders listener. Any other tab (desks, invoices, analytics, tracking…) starts it.
const NO_ORDERS_TABS = new Set(['dashboard', 'customers', 'telecaller', 'chat', 'todo', 'calendar', 'staff',
    'complaints', 'quick-order', 'rewards', 'referrals', 'products', 'settings', 'data-usage', 'backup', 'escalations', 'approvals']);
import { PHONE_SCREENS, PHONE_NATIVE } from '../../../mobile/phoneScreens';

const LazyFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
  </div>
);

const CustomerContent = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();

    if (customerId) {
        return (
            <div className="p-6 h-full">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
                    ← Back
                </Button>
                <CustomerProfile customerId={customerId} />
            </div>
        );
    }

    return (
        <div className="p-6 h-full space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Customer Intelligence</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <CustomerList />
                </div>
                <div className="space-y-6">
                    <UploadCustomers />
                </div>
            </div>
        </div>
    );
};

const DashboardLayout = ({ user, onLogout, initialTab = 'dashboard' }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(initialTab);
    // useState only reads its argument on the FIRST render. Every tab route renders
    // this same component, so React re-used the instance and the tab never changed:
    // navigate('/complaints') from anywhere in the app swapped the URL and left the
    // user staring at the page they were already on. Following the prop fixes that.
    // A sidebar click sets the tab and then navigates, so this lands on the value it
    // already holds and does nothing; sub-tabs like 'orders-pending' keep the same
    // route, so their prop never changes and they are left alone.
    React.useEffect(() => { setActiveTab(initialTab); }, [initialTab]);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    // Desktop sidebar (owner, 30 Oct 2026 — more room for the order tables):
    //   'full'   — the usual 256 px menu
    //   'icons'  — an 80 px rail of icons
    //   'hidden' — gone; slide the mouse to the left edge (or press the menu button) to peek
    // Auto-hide is the default; each computer remembers its own choice.
    const [sidebarMode, setSidebarModeState] = useState(() => {
        try { const m = localStorage.getItem('sidebarMode'); if (['full', 'icons', 'hidden'].includes(m)) return m; } catch { /* storage blocked */ }
        return 'hidden';
    });
    const [closedStyle, setClosedStyle] = useState(() => {
        try { const m = localStorage.getItem('sidebarClosedStyle'); if (m === 'icons' || m === 'hidden') return m; } catch { /* storage blocked */ }
        return 'hidden';
    });
    const [sidebarPeek, setSidebarPeek] = useState(false);
    const peekTimer = React.useRef(null);
    const setSidebarMode = (m) => {
        setSidebarModeState(m); setSidebarPeek(false);
        if (m !== 'full') { setClosedStyle(m); try { localStorage.setItem('sidebarClosedStyle', m); } catch { /* ignore */ } }
        try { localStorage.setItem('sidebarMode', m); } catch { /* ignore */ }
    };
    const isSidebarOpen = sidebarMode === 'full';
    const openPeek = () => { clearTimeout(peekTimer.current); setSidebarPeek(true); };
    const closePeek = () => { clearTimeout(peekTimer.current); peekTimer.current = setTimeout(() => setSidebarPeek(false), 250); };

    // Keep ONE 'orders' realtime listener warm for the whole signed-in session.
    // RTDB shares one sync per path, so every desk (pre-shipment, India Post,
    // DTDC, Ekart, Archived, Cancelled) and every re-navigation now reads the
    // orders tree from the synced cache instead of re-downloading the full
    // ~3.3MB tree on each mount — the dominant RTDB bandwidth cost. Released
    // only on full sign-out (DashboardLayout unmount).
    //
    // Started LAZILY (29 Oct 2026): only once the user opens a screen that uses the
    // orders tree. The tree is ~9.5 MB, and telecallers, support, chat and To-Do users
    // never open a desk — they no longer pay for it at every login. Screens that read
    // it on their own (readAllOrdersOnce) still fall back to a single read.
    const ordersUnsub = React.useRef(null);
    React.useEffect(() => {
        if (ordersUnsub.current || NO_ORDERS_TABS.has(activeTab)) return;
        ordersUnsub.current = useOrderStore.getState().subscribeOrders();
    }, [activeTab]);
    React.useEffect(() => () => { ordersUnsub.current?.(); ordersUnsub.current = null; }, []);

    useTeamAppManifest();   // installable as the team app (signed-in screens only)

    // Sync activeTab with URL path
    React.useEffect(() => {
        const path = location.pathname.split('/')[1];
        const validTabs = ['dashboard', 'customers', 'telecaller', 'orders', 'products', 'settings', 'parser', 'chat', 'referrals', 'dtdc', 'india-post', 'shiprocket', 'ekart', 'archived', 'cancelled', 'abandoned', 'escalations', 'tracking', 'invoices', 'labels', 'calendar', 'staff', 'todo', 'insights', 'quick-order', 'complaints', 'rewards', 'data-usage', 'backup', 'approvals'];

        if (validTabs.includes(path)) {
            setActiveTab(path);
        } else if (location.pathname === '/') {
            setActiveTab('dashboard');
        }
    }, [location]);
    // Destructure useChat with renamed call variables to avoid collision
    const {
        activeChats, minimizedChats, openChat, currentUser,
        activeCall, localStream, remoteStream, endCall, answerCall,
        toggleMute: toggleCallMute, toggleVideo, shareScreen, isMuted: isCallMuted, isVideoEnabled
    } = useChat();

    const effectiveUser = user || currentUser;
    const { activeAlarmTask, handleSnooze, handlePostpone, handleComplete, isMuted, toggleMute, stopSoundOnly } = useReminderSystem(effectiveUser);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


    React.useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Orders come straight from the ONE shared listener opened above
    // (orderStore). This used to be a second onValue('orders') here, torn down
    // and re-created on every sidebar tab change, re-downloading the whole
    // orders tree (4MB+) each time.
    //
    // Read the store DIRECTLY — do not copy it into local state. OrderList
    // writes its `orders` prop back into the same store, so mirroring
    // store -> state -> prop -> store is an infinite render loop. Passing the
    // store's own array keeps that write a no-op (same reference).
    const orders = useOrderStore((s) => s.orders);

    // Role-based capability check (fail-closed). The real enforcement point —
    // sidebar hiding is only cosmetic; this gates what actually renders.
    const hasPermission = (permKey) => canUser(effectiveUser, permKey);

    // Orders for analytics/finance exclude quarantined test/junk orders.
    // The Orders desk still gets the full set (it has a "Show quarantined" toggle).
    const reportOrders = useMemo(() => orders.filter(o => !o.excludeFromReports), [orders]);

    // Phone: the phone screen for this tab when one exists; otherwise the
    // desktop (advanced) screen under a slim strip saying so.
    const renderContent = () => {
        if (!isMobile) return renderDesktop();
        const phone = PHONE_SCREENS[activeTab];
        if (phone) {
            const Screen = phone.load;
            return hasPermission(phone.capability)
                ? <Suspense fallback={<LazyFallback />}><Screen /></Suspense>
                : <div className="p-6 text-red-500">Access Denied</div>;
        }
        if (PHONE_NATIVE.has(activeTab) || String(activeTab).startsWith('orders-')) return renderDesktop();
        return (
            <>
                <div className="mx-3 mt-2 mb-1 px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-100 text-[11px] text-indigo-800 font-semibold">
                    Advanced screen — easiest on a computer. The everyday jobs are in Orders, India Post and the bottom bar.
                </div>
                {renderDesktop()}
            </>
        );
    };

    const renderDesktop = () => {
        switch (activeTab) {
            case 'dashboard': return <Suspense fallback={<LazyFallback />}><DashboardOverview /></Suspense>;

            case 'orders':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><div className="p-3 h-full"><OrderList orders={orders} initialFilterStatus="all" /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'orders-pending':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><div className="p-3 h-full"><OrderList orders={orders} initialFilterStatus="pending" /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'orders-shipped':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><div className="p-3 h-full"><OrderList orders={orders} initialFilterStatus="shipped" /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'orders-delivered':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><div className="p-3 h-full"><OrderList orders={orders} initialFilterStatus="delivered" /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'invoices':
                return hasPermission('canViewFinance') ? (
                    <Suspense fallback={<LazyFallback />}><FinanceLayout orders={reportOrders} /></Suspense>
                ) : <div className="p-6 text-red-500">Access Denied</div>;

            case 'labels':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><div className="p-3 h-full"><OrderList orders={orders} initialFilterStatus="processing" /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'products':
                return hasPermission('canViewProducts') ? <Suspense fallback={<LazyFallback />}><div className="p-6 h-full"><ProductsInterface user={effectiveUser} /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'parser':
                return hasPermission('canViewParser') ? <Suspense fallback={<LazyFallback />}><div className="p-6 h-full"><ParserInterface user={effectiveUser} /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;
            case 'settings':
                return hasPermission('canManageSettings') ? <Suspense fallback={<LazyFallback />}><div className="p-6 h-full"><SettingsInterface /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'customers':
                return hasPermission('canViewCustomers') ? <CustomersProvider><CustomerContent /></CustomersProvider> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'telecaller':
                return hasPermission('canViewTelecallerWorkspace') ? <Suspense fallback={<LazyFallback />}><TelecallerWorkspace /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'chat':
                if (!hasPermission('canViewChat')) return <div className="p-6 text-red-500">Access Denied</div>;
                return (
                    <div className="flex h-[calc(100vh-4rem)] bg-transparent">
                        <div className={`h-full ${isMobile && activeChats.length > 0 ? 'hidden' : 'w-full md:w-[340px] block'}`}>
                            <ChatList onSelectChat={(chat) => openChat({ ...chat, id: chat.targetUserId })} />
                        </div>
                        <div className={`flex-1 h-full flex flex-col relative p-4 pl-0 ${isMobile && activeChats.length === 0 ? 'hidden' : 'block'}`}>
                            {activeChats.length > 0 ? (
                                <ChatWindow activeChat={activeChats[0]} isFullPage={true} isMobile={isMobile} onClose={() => { }} />
                            ) : (
                                <div className="text-slate-400 hidden md:flex flex-col items-center justify-center h-full text-center">
                                    <div className="w-24 h-24 bg-white rounded-full shadow-floating flex items-center justify-center mb-4">
                                        <span className="material-symbols-outlined text-4xl text-slate-300">chat</span>
                                    </div>
                                    <p className="font-bold text-lg text-slate-600">Select a conversation</p>
                                    <p className="text-sm mt-1">Choose a chat to start messaging</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'todo':
                return hasPermission('canViewTodo') ? <Suspense fallback={<LazyFallback />}><TaskBoard currentUser={effectiveUser} /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'calendar':
                return hasPermission('canViewCalendar') ? <Suspense fallback={<LazyFallback />}><OrganizationCalendar currentUser={effectiveUser} /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'staff':
                return hasPermission('canViewStaff') ? <Suspense fallback={<LazyFallback />}><StaffContainer currentUser={effectiveUser} /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'complaints':
                return hasPermission('canViewComplaints') ? <Suspense fallback={<LazyFallback />}><ComplaintsLayout /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'quick-order':
                return hasPermission('canViewComplaints') ? <Suspense fallback={<LazyFallback />}><QuickOrderAdmin /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'approvals':
                return hasPermission('canViewApprovals') ? <Suspense fallback={<LazyFallback />}><ApprovalDesk /></Suspense> : <div className="p-6 text-red-500">Only managers and admins use the Approval Desk.</div>;
            case 'escalations':
                return hasPermission('canViewCourierEscalations') ? <Suspense fallback={<LazyFallback />}><CourierEscalationDashboard /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            // Which module is spending the Firebase bill (admin diagnostics).
            case 'backup':
                return <Suspense fallback={<LazyFallback />}><div className="p-6 h-full overflow-y-auto"><BackupRestorePanel /></div></Suspense>;
            case 'data-usage':
                return <Suspense fallback={<LazyFallback />}><div className="p-6 h-full overflow-y-auto"><RtdbUsagePanel /></div></Suspense>;

            case 'analytics':
                return hasPermission('canViewAnalytics') ? (
                    <Suspense fallback={<LazyFallback />}>
                        <div className="p-6 h-full space-y-6">
                            <AnalyticsDashboard />
                            <div className="border-t border-slate-200 pt-6">
                                <InteraktAnalytics />
                            </div>
                        </div>
                    </Suspense>
                ) : <div className="p-6 text-red-500">Access Denied</div>;

            case 'india-post':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><IndiaPostManager /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'dtdc':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><DTDCManager /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'ekart':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><EkartManager /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'shiprocket':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><ShiprocketManager /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'archived':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><ArchivedOrders /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'abandoned':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><AbandonedOrders /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'cancelled':
                return hasPermission('canViewOrders') ? <Suspense fallback={<LazyFallback />}><CancelledOrders /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'rewards':
                return hasPermission('canViewRewards') ? <Suspense fallback={<LazyFallback />}><RewardsModule /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'referrals':
                return hasPermission('canViewReferrals') ? <Suspense fallback={<LazyFallback />}><ReferralDashboard /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'tracking':
                return hasPermission('canViewTracking') ? <Suspense fallback={<LazyFallback />}><TrackingDashboard /></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            case 'insights':
                return hasPermission('canViewAnalytics') ? <Suspense fallback={<LazyFallback />}><div className="p-6 h-full"><CustomerInsights orders={reportOrders} /></div></Suspense> : <div className="p-6 text-red-500">Access Denied</div>;

            default: return <div className="p-6">Feature Coming Soon</div>;
        }
    };

    return (
        <NotificationManager userId={effectiveUser?.uid}>
            {(notifications) => (
                <div className="flex h-screen bg-slate-50 overflow-hidden font-sans text-slate-900">
                    {/* Mobile Sidebar Overlay */}
                    {isMobileSidebarOpen && (
                        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsMobileSidebarOpen(false)} />
                    )}

                    {/* Auto-hide: a thin strip on the left edge brings the menu out on hover. */}
                    {!isMobile && sidebarMode === 'hidden' && !sidebarPeek && (
                        <div className="fixed inset-y-0 left-0 w-2 z-40" onMouseEnter={openPeek} aria-hidden="true" />
                    )}

                    {/* Sidebar */}
                    <div
                        onMouseEnter={sidebarMode === 'hidden' ? openPeek : undefined}
                        onMouseLeave={sidebarMode === 'hidden' ? closePeek : undefined}
                        className={`
                        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
                        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                        ${isMobile ? '' : sidebarMode === 'hidden'
                            ? (sidebarPeek ? 'md:translate-x-0 md:shadow-2xl' : 'md:-translate-x-full')
                            : `md:relative md:translate-x-0 ${sidebarMode === 'full' ? 'md:w-64' : 'md:w-20'}`}
                    `}>
                        <Sidebar
                            activeTab={activeTab}
                            onTabChange={(tab) => {
                                setActiveTab(tab);
                                setIsMobileSidebarOpen(false);
                                setSidebarPeek(false);
                                // Sync URL for top level tabs
                                const routes = ['dashboard', 'customers', 'telecaller', 'orders', 'products', 'settings', 'parser', 'chat', 'referrals', 'dtdc', 'india-post', 'shiprocket', 'ekart', 'archived', 'cancelled', 'abandoned', 'tracking', 'calendar', 'staff', 'todo', 'insights', 'quick-order', 'complaints', 'escalations', 'rewards', 'data-usage', 'backup', 'approvals'];
                                if (routes.includes(tab)) {
                                    navigate(`/${tab}`);
                                } else if (tab.startsWith('orders-')) {
                                    navigate('/orders');
                                }
                            }}
                            onLogout={onLogout}
                            collapsed={!isMobile && sidebarMode === 'icons'}
                            onClose={() => setIsMobileSidebarOpen(false)}
                        />
                    </div>

                    <div className="flex-1 flex flex-col min-w-0 transition-all duration-300">
                        <Header
                            user={effectiveUser}
                            isMuted={isMuted}
                            toggleMute={toggleMute}
                            onToggleSidebar={() => {
                                if (window.innerWidth < 768) setIsMobileSidebarOpen(!isMobileSidebarOpen);
                                else setSidebarMode(isSidebarOpen ? closedStyle : 'full');
                            }}
                            sidebarMode={sidebarMode}
                            onSidebarMode={setSidebarMode}
                            // Inject Notification Bell
                            rightElement={(
                                <>
                                    {/* The signed-in agent's own phone link (hidden if they never connected one). */}
                                    {/* A phone on the desktop layout can switch back to the phone layout. */}
                                    {isNarrowScreen() && getViewMode() === 'desktop' && (
                                        <button type="button" onClick={() => setViewMode('phone')} className="px-3 py-1.5 rounded-full bg-purple-600 text-white text-xs font-black">Phone view</button>
                                    )}
                                    <div className="hidden sm:block"><PhoneStatusPill /></div>
                                    <div className="sm:hidden"><PhoneStatusPill compact /></div>
                                    <NotificationBell notifications={notifications} userId={effectiveUser?.uid} />
                                </>
                            )}
                        />

                        <main className={`flex-1 overflow-y-auto overflow-x-hidden relative ${isMobile ? 'pb-20' : ''}`}>
                            {/* Full-width, adaptive content — uses the whole available area
                                instead of a fixed centred column. */}
                            <div className="w-full h-full">
                                {renderContent()}
                            </div>
                        </main>
                    </div>

                    {/* Live call dock — the agent's current phone call, controlled from the system (ALL pages) */}
                    <LiveCallDock />
                    {/* Caller 4.30: incoming-call pop-up + the live call bar (tc/live/{uid}) */}
                    <LiveCallLayer />
                    {/* Step-by-step confirmation of every call sent to the phone (ALL pages) */}
                    <DialTracker />

                    {/* Global Chat System */}
                    {activeTab !== 'chat' && <ChatSystem />}
                    {isMobile && <MobileNavbar />}
                    <AlarmOverlay
                        task={activeAlarmTask}
                        onSnooze={handleSnooze}
                        onPostpone={handlePostpone}
                        onComplete={handleComplete}
                        onStopSound={stopSoundOnly}
                    />

                    {/* Chat Notification Toasts — visible on ALL pages */}
                    <ChatNotificationProvider
                        onOpenChat={(toast) => {
                            if (toast.chatId) {
                                navigate('/chat', { state: { openChatId: toast.chatId, chatName: toast.chatName, isGroup: toast.isGroup, isChannel: toast.isChannel, targetUserId: toast.targetUserId } });
                            }
                        }}
                    />

                </div>
            )}
        </NotificationManager>
    );
};

export default DashboardLayout;
